import { Injectable, inject } from '@angular/core';

import { EMPTY, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { CommandProgressStoreSelectors } from '@era/stores';
import { Store } from '@ngrx/store';
import { CommandProgress as MdmCommandProgress, ErrorChain as MdmErrorChain, RequestType } from '@rocksoft/mdm-sdk-v2';
import { isNotUndefined } from '@rocksoft/shared';
import { CommandProgress } from '../../viewmodels/apple/signalr/command-context.model';
import { ErrorChain } from '../../viewmodels/apple/signalr/error-chain.model';

@Injectable({
  providedIn: 'root',
})
export class AppleCommandProgressService {
  private readonly store = inject(Store);

  /**
   * Track states of certain command type for a specific device.
   * @param udid the unique device identifier
   * @param requestType the command's request type
   */
  commandsOfRequestType(udid: string | null, requestType: RequestType): Observable<CommandProgress> {
    if (udid == null) {
      return EMPTY;
    }
    return this.store
      .select(CommandProgressStoreSelectors.selectCommandProgressByUdidAndRequestType(udid, requestType))
      .pipe(
        filter(isNotUndefined),
        map((x) => this.map(x))
      );
  }

  map(commandProgress: MdmCommandProgress): CommandProgress {
    return {
      udid: commandProgress.udid,
      commandUUID: commandProgress.commandUUID,
      status: commandProgress.status ?? '',
      requestType: commandProgress.requestType,
      // We sometimes receive a `string` here instead of the promised `Date`, so we map it using custom logic.
      createdAt: this.mapDate(commandProgress.created),
      errorChain: commandProgress.errorChain?.map(this.mapErrorChain),
    };
  }

  mapDate(date: unknown): Date | undefined {
    let dateStr: string;
    if (date instanceof Date) {
      return date;
    } else if (typeof date === 'string') {
      dateStr = date;
    } else {
      return undefined;
    }

    return new Date(dateStr);
  }

  mapErrorChain = (errorChain: MdmErrorChain): ErrorChain => {
    return {
      errorCode: errorChain.errorCode ?? 0,
      errorDomain: errorChain.errorDomain ?? '',
      localizedDescription: errorChain.localizedDescription ?? '',
      usEnglishDescription: errorChain.usEnglishDescription ?? '',
    };
  };
}
